import { useQuery } from '@tanstack/react-query';
import { Stack, Text, Button, Group } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { admin } from '@repletoryreach/sdk-api';
import { ItnHealthCards } from './components/ItnHealthCards';
import { IconRefresh } from '../components/icons';
import { useT } from '../../../i18n/I18nProvider';
import '../components/billing.css';

/** Credentials change rarely, so a minute is often enough to catch an edit. */
const REFETCH_MS = 60_000;

/**
 * Whether the ITN handlers are fail-closed here.
 *
 * `WebhookPolicy` rejects an unsigned callback in Production and accepts it
 * with a warning everywhere else, so the banner has to say which of those the
 * operator is looking at. The console's build mode is the only signal the
 * browser has, which is why the copy speaks about the environment rather than
 * about a specific service instance.
 */
function signatureEnforced(): boolean {
  return import.meta.env.MODE === 'production';
}

export default function ItnPage() {
  const t = useT();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'integrations'],
    queryFn: () => admin.listIntegrations(),
    refetchInterval: REFETCH_MS
  });

  const enforced = signatureEnforced();

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.itn.eyebrow')}
        title={t('billing.itn.title')}
        description={t('billing.itn.description')}
        actions={
          <Group gap="sm" align="center" wrap="nowrap">
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('billing.itn.autoRefresh', { seconds: REFETCH_MS / 1000 })}
            </Text>
            <Button size="sm" variant="outline" leftSection={<IconRefresh />} loading={isFetching} onClick={() => void refetch()}>
              {t('billing.itn.refreshNow')}
            </Button>
          </Group>
        }
      />

      {/* Without this the three cards all read "Not configured" on a failed
          request, which on the payments page is the worst thing they could say. */}
      {error && (
        <Alert color="error">
          <Stack gap="sm">
            <Text role="bodySmall">{t('billing.itn.loadError')}</Text>
            <Group>
              <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
            </Group>
          </Stack>
        </Alert>
      )}

      <Alert
        color={enforced ? 'info' : 'warning'}
        title={enforced ? t('billing.itn.signature.enforcedTitle') : t('billing.itn.signature.permissiveTitle')}
      >
        {enforced ? t('billing.itn.signature.enforcedBody') : t('billing.itn.signature.permissiveBody')}
      </Alert>

      <ItnHealthCards rows={error ? [] : (data?.data ?? [])} loading={isLoading} />

      <Text role="caption" c="var(--rr-color-text-muted)">
        {t('billing.itn.footnote')}
      </Text>
    </Stack>
  );
}
